import React, { useState, useEffect } from 'react';
import revisorService from '../../services/revisorService';
import gradeService from '../../services/gradeService';
import { formatGrade, getGradeColor } from '../../utils/gradeColors';

const StudentGradeReportModal = ({ isOpen, onClose, student, schoolData }) => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSemesters, setSelectedSemesters] = useState([]);
  const [semesterModalOpen, setSemesterModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen && student) {
      loadReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, student]);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const professionTree = await revisorService.getReviews();
      let studentReviews = [];
      professionTree.forEach(profession => {
        profession.learners.forEach(learner => {
          if (String(learner.learnerId) === String(student.id)) {
            learner.semesters.forEach(r => {
              if (r.reviewStatus === 'CHECKED' || r.reviewStatus === 'FINALIZED') {
                studentReviews.push(r);
              }
            });
          }
        });
      });

      // Now fetch definitive grades for each checked/finalized semester
      const reportItems = [];
      
      for (const review of studentReviews) {
        // Find which school this belongs to
        let semesterInfo = null;
        let schoolInfo = null;
        
        if (schoolData) {
          for (const school of schoolData) {
            const sem = school.semesters.find(s => String(s.id) === String(review.semesterId));
            if (sem) {
              semesterInfo = sem;
              schoolInfo = school;
              break;
            }
          }
        }
        
        // Fetch definitive grades directly (these are the ones the revisor validated)
        const definitiveRows = await gradeService.getSemesterDefinitiveGrades(
          review.semesterId,
          student.id,
          semesterInfo?.classId ?? semesterInfo?.class_id ?? null,
        );
        
        reportItems.push({
          semesterId: review.semesterId,
          semesterName: semesterInfo ? semesterInfo.name : 'Unknown Semester',
          className: semesterInfo ? semesterInfo.className : null,
          schoolName: schoolInfo ? schoolInfo.schoolName : 'Unknown School',
          status: review.reviewStatus,
          revisorName: review.reviewedBy ? `${review.reviewedBy.firstName} ${review.reviewedBy.lastName}` : null,
          updatedAt: review.updatedAt,
          subjects: definitiveRows
            .filter(row => row.definitiveGrade !== null && !Number.isNaN(row.definitiveGrade))
            .map(row => ({
               subjectId: row.subjectId,
               subjectName: row.subjectName || row.subjectCode || 'Unknown Subject',
               gradeValue: row.definitiveGrade
            }))
        });
      }
      
      setReportData(reportItems);
      setSelectedSemesters(reportItems.map(item => item.semesterId));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleSemester = (semesterId) => {
    setSelectedSemesters(prev => 
      prev.includes(semesterId)
        ? prev.filter(id => id !== semesterId)
        : [...prev, semesterId]
    );
  };

  const selectAllSemesters = () => {
    setSelectedSemesters(reportData.map(item => item.semesterId));
  };

  const deselectAllSemesters = () => {
    setSelectedSemesters([]);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/50 backdrop-blur-sm print:bg-white print:p-0">
      
      {/* Hide other elements when printing */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }
          html, body {
            background: #fff !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body * {
            visibility: hidden;
          }
          .print-container, .print-container * {
            visibility: visible;
          }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: auto;
            overflow: visible !important;
            box-shadow: none !important;
            padding: 0 !important;
            max-height: none !important;
            border-radius: 0 !important;
          }
          .no-print {
            display: none !important;
          }
          .grade-report-content {
            padding: 0 !important;
            overflow: visible !important;
          }
          .grade-report-print-header {
            display: flex !important;
            justify-content: space-between;
            align-items: flex-end;
            text-align: left !important;
            margin-bottom: 12px !important;
            padding-bottom: 8px !important;
            border-bottom: 2px solid #da291c !important;
          }
          .grade-report-print-title {
            font-size: 18px !important;
            line-height: 1.2 !important;
            margin: 0 0 2px 0 !important;
          }
          .grade-report-print-meta {
            font-size: 10px !important;
            line-height: 1.3 !important;
            margin: 0 !important;
          }
          .grade-semester-list > * + * {
            margin-top: 10px !important;
          }
          .grade-semester-card {
            border: 1px solid #ddd8d2 !important;
            box-shadow: none !important;
            break-inside: avoid;
            page-break-inside: avoid;
            margin-bottom: 0 !important;
          }
          .grade-semester-header {
            padding: 8px 10px !important;
            background: #f7f4f0 !important;
            border-bottom: 1px solid #ddd8d2 !important;
          }
          .grade-semester-title {
            font-size: 13px !important;
            line-height: 1.2 !important;
          }
          .grade-semester-school {
            font-size: 9px !important;
            margin-top: 2px !important;
          }
          .grade-status-pill {
            padding: 3px 7px !important;
            font-size: 8px !important;
            letter-spacing: 0 !important;
            border-color: #ddd8d2 !important;
          }
          .grade-report-table th {
            padding: 5px 10px !important;
            font-size: 8px !important;
            letter-spacing: 0.02em !important;
          }
          .grade-report-table td {
            padding: 6px 10px !important;
            font-size: 10px !important;
          }
          .grade-report-table .grade-value {
            font-size: 14px !important;
          }
        }
      `}</style>
      
      <div className="print-container bg-white rounded-2xl shadow-brand-xl border border-pale-dawn w-full max-w-4xl max-h-[90vh] flex flex-col relative">
        
        <div className="no-print p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-xl">
          <h2 className="text-xl font-bold text-gray-800 flex items-center">
            <div className="w-10 h-10 bg-sunrise-red/10 rounded-lg flex items-center justify-center text-sunrise-red mr-3">
              <i className="fas fa-file-invoice"></i>
            </div>
            Checked Grades Report - {student?.firstName} {student?.lastName}
          </h2>
          
          <div className="flex items-center space-x-3">
            {/*
            <button onClick={exportPrint} className="px-4 py-2 bg-sunrise-red text-white flex items-center rounded-lg hover:bg-red-700 transition">
              <i className="fas fa-print mr-2"></i> Print Report
            </button>
            */}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
          
        </div>

        {/* Semester Filter Dropdown Button */}
        {!loading && reportData.length > 0 && (
          <div className="no-print p-4 bg-gradient-to-r from-ice-white to-pale-dawn/30 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-grey-dusk flex items-center">
                  <i className="fas fa-filter mr-2 text-sunrise-red"></i>
                  Filter Semesters to Print
                </h3>
                <p className="text-sm opacity-90 mt-1">
                  <i className="fas fa-chart-bar mr-1 text-blue-400"></i>
                  {selectedSemesters.length} semester(s) selected
                </p>
              </div>
              <div className="relative">
                <button
                  onClick={() => setSemesterModalOpen(!semesterModalOpen)}
                  className="bg-white border border-gray-200 hover:border-gray-300 shadow-sm rounded-lg py-2 px-4 transition-all duration-200 flex items-center justify-center font-medium text-grey-dusk"
                  title="Filter Semesters"
                >
                  <i className="fas fa-filter mr-2 text-sunrise-red"></i> 
                  Select Semesters
                  <i className={`fas fa-chevron-${semesterModalOpen ? 'up' : 'down'} ml-3 text-xs text-gray-400`}></i>
                </button>
                
                {/* Semester Dropdown Content */}
                {semesterModalOpen && (
                  <>
                    {/* Invisible backdrop to catch outside clicks */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setSemesterModalOpen(false)}
                    ></div>
                    
                    <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 w-80 max-h-96 flex flex-col">
                      <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-sunrise-red/5 to-sunrise-600/5 rounded-t-lg">
                        <div className="flex gap-2">
                          <button
                            onClick={selectAllSemesters}
                            className="flex-1 px-3 py-2 text-sm bg-sunrise-red/10 text-sunrise-red rounded-lg hover:bg-sunrise-red/20 transition-colors"
                          >
                            <i className="fas fa-check-double mr-1"></i>
                            Select All
                          </button>
                          <button
                            onClick={deselectAllSemesters}
                            className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                          >
                            <i className="fas fa-times-circle mr-1"></i>
                            Clear All
                          </button>
                        </div>
                      </div>
                      
                      <div className="p-4 overflow-y-auto space-y-2">
                        {reportData.map((data, idx) => (
                          <label key={idx} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors border border-transparent hover:border-gray-100">
                            <input 
                              type="checkbox" 
                              checked={selectedSemesters.includes(data.semesterId)} 
                              onChange={() => toggleSemester(data.semesterId)}
                              className="w-4 h-4 text-sunrise-red border-gray-300 rounded focus:ring-sunrise-red focus:ring-2"
                            />
                            <span className="font-medium text-grey-dusk flex-1 flex items-center justify-between">
                              <span>
                                {data.semesterName}
                                {data.className && (
                                  <span className="text-sunrise-red ml-2 text-sm">- {data.className}</span>
                                )}
                              </span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grade-report-content p-5 sm:p-6 overflow-y-auto flex-1 relative z-0 rounded-b-xl bg-ice-white/40">
          {/* Print Header */}
          <div className="grade-report-print-header hidden print:block mb-4 border-b-2 border-sunrise-red pb-3">
            <div>
              <h1 className="grade-report-print-title text-2xl font-bold text-gray-800">Official Grade Report</h1>
              <p className="grade-report-print-meta text-sm font-semibold text-gray-700">
                {student?.firstName} {student?.lastName}
              </p>
            </div>
            <p className="grade-report-print-meta text-xs text-gray-500">
              Generated on {new Date().toLocaleDateString()}
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sunrise-red mb-4"></div>
              Loading certified grades...
            </div>
          ) : reportData.length === 0 ? (
            <div className="text-center py-20 text-gray-500 bg-gray-50 rounded-xl border border-gray-200 border-dashed">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl opacity-50">
                <i className="fas fa-search"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">No certified grades</h3>
              <p>There are no checked or finalized grades available for this student yet.</p>
            </div>
          ) : (
            <div className="grade-semester-list space-y-4">
              {reportData.filter(data => selectedSemesters.includes(data.semesterId)).map((data, idx) => (
                <div key={idx} className="grade-semester-card bg-white border text-gray-800 border-gray-200 rounded-lg overflow-hidden shadow-sm break-inside-avoid">
                  <div className="grade-semester-header bg-white px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                    <div>
                      <h3 className="grade-semester-title font-bold text-lg text-gray-800 flex items-center">
                        <i className="fas fa-calendar-alt text-sunrise-red mr-2 print:hidden"></i>
                        {data.semesterName}
                        {data.className && (
                          <span className="text-sunrise-red ml-2">- {data.className}</span>
                        )}
                      </h3>
                      <p className="grade-semester-school text-xs text-gray-500 font-medium mt-1">{data.schoolName}</p>
                    </div>
                    <div className="text-right">
                      <span className={`grade-status-pill inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase ${data.status === 'FINALIZED' ? 'bg-emerald-100 text-emerald-700' : 'bg-green-100 text-green-700'}`}>
                        <i className={`fas fa-check-circle mr-1.5 ${data.status === 'FINALIZED' ? 'text-emerald-500' : 'text-green-500'} print:hidden`}></i>
                        {data.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-0">
                    <table className="grade-report-table w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50/50">
                          <th className="py-2 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider border-b border-gray-200">Subject</th>
                          <th className="py-2 px-4 font-semibold text-gray-600 text-xs uppercase tracking-wider border-b border-gray-200 text-right" colSpan={2}>Definitive Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.subjects.length > 0 ? (
                          data.subjects.map((subj, sIdx) => {
                            return (
                              <tr key={sIdx} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors">
                                <td className="py-2.5 px-4 text-gray-800 font-medium">{subj.subjectName}</td>
                                <td className="grade-value py-2.5 px-4 text-right text-lg font-bold" colSpan={2} style={{ color: getGradeColor(subj.gradeValue) }}>
                                  {formatGrade(subj.gradeValue)}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="3" className="py-6 px-6 text-center text-gray-500 italic">
                              No definitive grades listed for this semester.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentGradeReportModal;
